import { Fragment, useState } from 'react'
import { useLocalObservable } from 'mobx-react-lite'
import { type ActionItem, Divider, type IColumn, Tooltip } from 'shared/ui'
import { DayjsFormats, logger } from 'shared/lib'
import dayjs from 'shared/lib/dayjs'
import { uiStore } from 'shared/store/uiStore'
import { SwitchAction } from 'shared/ui/SwitchAction'
import { type IFullWorkflowData, type IWorkflow } from 'entities/Workflow'
import { useWorkflowListContext } from 'pages/workflows/context/workflowListContext'
import { WorkflowStatusBadge } from 'pages/workflows/ui'
import { workflowOperation } from 'pages/workflows/store/operationStore'

export type IWorkflowRow = {
  id: string
  workflow: IWorkflow
}

const COLUMNS: IColumn<IWorkflowRow>[] = [
  {
    field: 'name',
    name: 'Name',
    minWidth: '145px',
    maxWidth: '245px',
    isDisabledChangeVisible: true,
    renderRowCell: ({ workflow }) => workflow.name,
  },
  {
    field: 'status',
    name: 'Status',
    minWidth: '84px',
    maxWidth: '120px',
    isDisabledChangeVisible: true,
    renderRowCell: ({ workflow }) => (
      <Tooltip label={workflow.isInvalid && 'Suspicious content detected'} placement={'top'}>
        <WorkflowStatusBadge isInvalid={workflow.isInvalid} active={workflow.enabled} />
      </Tooltip>
    ),
  },
  {
    field: 'enrolled',
    name: 'Enrolled',
    minWidth: '84px',
    maxWidth: '120px',
    isDisabledChangeVisible: true,
    renderRowCell: ({ workflow }) => workflow.executionsCount,
  },
  {
    field: 'created_at',
    name: 'Created',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ workflow }) => dayjs(workflow.createdAt).format(DayjsFormats.fullWithAtDash2),
  },
  {
    field: 'last_modified',
    name: 'Last modified',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ workflow }) =>
      !!workflow.updatedAt ? (
        dayjs(workflow.updatedAt).format(DayjsFormats.fullWithAtDash2)
      ) : (
        <Fragment />
      ),
  },
]

export const useWorkflowTable = () => {
  const [columnsIds, setColumnIds] = useState(() => COLUMNS.map(({ field }) => field))
  const { items, loadData } = useWorkflowListContext()

  const duplications = useLocalObservable(() => new Set<string>())
  const deletions = useLocalObservable(() => new Set<string>())

  const makeActivateAction = (workflow: IWorkflow): ActionItem | null => {
    if (workflow.isInvalid) return null

    let closeMenu: (() => void) | undefined

    const onTrigger = async () => {
      try {
        let payload: Partial<IFullWorkflowData> | null
        if (workflow.enabled) {
          payload = await workflowOperation.disable(workflow)
          closeMenu?.()
        } else {
          closeMenu?.()
          payload = await workflowOperation.enable(workflow)
        }
        if (payload) {
          workflow.syncOrigin(payload)
        }
      } catch (e) {
        logger.error(e)
      }
    }

    const content = (
      <>
        <SwitchAction
          size={'xSmall'}
          label={workflow.enabled ? 'Active' : 'Disabled'}
          active={workflow.enabled}
          onTrigger={onTrigger}
        />
        <Divider sx={{ marginX: '-4px', marginY: '5px' }} />
      </>
    )

    return {
      dropdownItemComponent: (onCloseMenu) => ((closeMenu = onCloseMenu), content),
    }
  }

  const makeEditAction = (workflow: IWorkflow): ActionItem => ({
    icon: 'edit',
    text: 'Edit',
    onAction: () => {
      uiStore.changeRoute({ path: `/workflows/view/${workflow.id}` })
    },
  })

  const makeDuplicateAction = (workflow: IWorkflow): ActionItem => ({
    icon: 'copy',
    text: 'Duplicate',
    buttonProps: {
      disabled: duplications.has(workflow.id) || deletions.has(workflow.id),
      loading: duplications.has(workflow.id),
    },
    onAction: () => {
      duplications.add(workflow.id)
      workflowOperation.duplicate(workflow).then((duplicated) => {
        duplications.delete(workflow.id)

        if (duplicated) uiStore.changeRoute({ path: `/workflows/view/${duplicated.id}` })
      })
    },
  })

  const makeDeleteAction = (workflow: IWorkflow): ActionItem => ({
    icon: 'delete',
    text: 'Delete',
    buttonProps: {
      disabled: deletions.has(workflow.id) || duplications.has(workflow.id),
      loading: deletions.has(workflow.id),
    },
    onAction: () => {
      deletions.add(workflow.id)
      workflowOperation.delete(workflow).then((deleted) => {
        deletions.delete(workflow.id)

        if (deleted) loadData()
      })
    },
  })

  const columns = COLUMNS
  const rows = items.map((workflow) => {
    const actions = [
      makeActivateAction(workflow),
      makeEditAction(workflow),
      makeDuplicateAction(workflow),
      makeDeleteAction(workflow),
    ].filter((item) => !!item) as ActionItem[]

    return { id: workflow.id, workflow, actionsProps: { actions } }
  })

  return { rows, columns, columnsIds, setColumnIds }
}
